import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Search, Star, Users, Clock } from 'lucide-react';

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(5, 5, 5, 0.4), rgba(5, 5, 5, 0.8)), url('https://images.unsplash.com/photo-1741920852842-42cd39cee882?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxUb2t5byUyMG5pZ2h0JTIwbmVvbiUyMGNpdHklMjBzdHJlZXR8ZW58MHx8fHwxNzcwMDgzMTk5fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-7xl mx-auto px-6 pt-32 pb-20 text-center relative z-10">
          <div className="space-y-8">
            <h1 
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white"
              style={{ fontFamily: 'Syne, sans-serif' }}
              data-testid="hero-title"
            >
              {t('hero.title')}
            </h1>
            
            <p 
              className="text-xl sm:text-2xl text-zinc-300 max-w-3xl mx-auto"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
              <Button
                onClick={() => navigate('/hosts')}
                className="bg-[#F9004D] hover:bg-[#D00040] text-white rounded-full px-8 py-6 text-lg font-bold shadow-[0_0_15px_rgba(249,0,77,0.4)] hover:shadow-[0_0_25px_rgba(249,0,77,0.6)] transition-all duration-300"
                data-testid="search-guides-button"
              >
                <Search className="mr-2 h-5 w-5" />
                {t('hero.searchHosts')}
              </Button>
              
              <Button
                onClick={() => navigate('/become-host')}
                className="bg-transparent border-2 border-white/20 hover:border-white/80 text-white rounded-full px-8 py-6 text-lg font-medium backdrop-blur-sm transition-all duration-300"
                data-testid="become-guide-button"
              >
                {t('hero.becomeHost')}
              </Button>
            </div>
          </div>
        </div>

        {/* Decorative glow */}
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-20 blur-[100px] pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(249,0,77,0.3) 0%, rgba(0,0,0,0) 70%)' }}
        />
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <h2 
            className="text-4xl font-bold text-center mb-16 text-white"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            {t('features.title')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Users className="h-12 w-12 text-[#F9004D]" />,
                title: t('features.localExperts.title'),
                description: t('features.localExperts.description')
              },
              {
                icon: <Star className="h-12 w-12 text-[#00F0FF]" />,
                title: t('features.verified.title'),
                description: t('features.verified.description')
              },
              {
                icon: <Clock className="h-12 w-12 text-[#D4AF37]" />,
                title: t('features.flexible.title'),
                description: t('features.flexible.description')
              }
            ].map((feature, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl bg-zinc-900/50 border border-white/10 hover:border-[#F9004D]/50 p-8 transition-all duration-500"
              >
                <div className="space-y-4">
                  <div className="flex justify-center">{feature.icon}</div>
                  <h3 className="text-2xl font-bold text-white text-center" style={{ fontFamily: 'Syne, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-zinc-400 text-center">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#050505] to-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 
            className="text-4xl sm:text-5xl font-bold text-white"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            {t('cta.ready')}
          </h2>
          <p className="text-xl text-zinc-300">{t('cta.subtitle')}</p>
          <Button
            onClick={() => navigate('/hosts')}
            className="bg-[#F9004D] hover:bg-[#D00040] text-white rounded-full px-12 py-6 text-lg font-bold shadow-[0_0_15px_rgba(249,0,77,0.4)] hover:shadow-[0_0_25px_rgba(249,0,77,0.6)] transition-all duration-300"
          >
            {t('cta.findHost')}
          </Button>
        </div>
      </section>
    </div>
  );
}